import { useMemo } from "react";

import type { DatapointRow } from "./api";

/**
 * Wizualny edytor warunków.
 *
 * Warunki decydują o całym przebiegu procesu, a pisane były ręcznie w JSON-ie —
 * z zagnieżdżeniami na cztery poziomy. To jedyne miejsce w panelu, którego
 * rekruter nie mógł tknąć bez programisty, i stąd brały się błędy, które potem
 * łapał dopiero walidator: literówka w kluczu pola, warunek o sobie samym,
 * koniunkcja nie do spełnienia.
 *
 * Model danych jest dokładnie ten sam co na backendzie (`core/conditions.py`),
 * więc edytor niczego nie tłumaczy — składa tę samą strukturę, którą i tak
 * trzeba by napisać ręcznie.
 */

export type Rule = { f: string; op: string; v?: unknown };
export type Group = { logic: "AND" | "OR"; rules: Node[] };
export type Node = Rule | Group;

export function isGroup(node: Node): node is Group {
  return typeof (node as Group).logic === "string";
}

/** Pola, od których warunek zależy — ten sam obchód co `conditions.referenced_fields`. */
export function referencedFields(node: unknown, acc = new Set<string>()): Set<string> {
  if (typeof node !== "object" || node === null) return acc;
  const candidate = node as Record<string, unknown>;
  if (typeof candidate.logic === "string") {
    for (const child of (candidate.rules as unknown[]) ?? []) referencedFields(child, acc);
  } else if (typeof candidate.f === "string" && candidate.f) {
    acc.add(candidate.f);
  }
  return acc;
}

type Arity = "none" | "one" | "many" | "window";

/** Lista z `conditions.OPERATORS` — kolejność od najczęściej używanych. */
const OPERATORS: { op: string; label: string; arity: Arity }[] = [
  { op: "equal_to", label: "równa się", arity: "one" },
  { op: "not_equal_to", label: "nie równa się", arity: "one" },
  { op: "is_one_of", label: "jest jedną z", arity: "many" },
  { op: "is_not_one_of", label: "nie jest żadną z", arity: "many" },
  { op: "is_empty", label: "jest puste", arity: "none" },
  { op: "is_not_empty", label: "jest wypełnione", arity: "none" },
  { op: "contains", label: "zawiera", arity: "one" },
  { op: "not_contains", label: "nie zawiera", arity: "one" },
  { op: "starts_with", label: "zaczyna się od", arity: "one" },
  { op: "ends_with", label: "kończy się na", arity: "one" },
  { op: "greater_than", label: "większe niż", arity: "one" },
  { op: "less_than", label: "mniejsze niż", arity: "one" },
  // Operatory dat liczone względem „dziś" — granica przesuwa się sama (np. wiek
  // z daty urodzenia, ważność dokumentu). „przeszła/przyszła" nie biorą wartości;
  // „w ciągu…" biorą okno „N jednostka" składane niżej (`WindowValue`).
  { op: "date_in_past", label: "to data przeszła (już minęła)", arity: "none" },
  { op: "date_in_future", label: "to data przyszła (jeszcze nie minęła)", arity: "none" },
  { op: "date_within_last", label: "jest w ciągu ostatnich…", arity: "window" },
  { op: "date_within_next", label: "jest w ciągu najbliższych…", arity: "window" },
];

/** Jednostki okna dat — etykieta po polsku, `unit` zgodny z `conditions._okno`. */
const WINDOW_UNITS: { unit: string; label: string }[] = [
  { unit: "day", label: "dni" },
  { unit: "week", label: "tygodni" },
  { unit: "month", label: "miesięcy" },
  { unit: "year", label: "lat" },
];

const ARITY = new Map(OPERATORS.map((entry) => [entry.op, entry.arity]));

function emptyRule(): Rule {
  return { f: "", op: "equal_to", v: "" };
}

interface Props {
  value: Node | null;
  onChange: (next: Node | null) => void;
  /** Pola, do których warunek może się odwoływać. */
  fields: DatapointRow[];
  /** Węzeł edytowany teraz — warunek o sobie samym nigdy się nie spełni. */
  selfKey: string;
}

export function ConditionEditor({ value, onChange, fields, selfKey }: Props) {
  if (value === null) {
    return (
      <div className="cond cond--empty">
        <p className="adm__muted">Brak warunku — pytanie jest zawsze aktywne.</p>
        <div className="cond__actions">
          <button className="btn btn--secondary" onClick={() => onChange(emptyRule())}>
            + Warunek
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => onChange({ logic: "AND", rules: [emptyRule()] })}
          >
            + Grupa
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cond">
      <NodeEditor
        node={value}
        fields={fields}
        selfKey={selfKey}
        onChange={onChange}
        onRemove={() => onChange(null)}
        depth={0}
      />
    </div>
  );
}

function NodeEditor({
  node,
  fields,
  selfKey,
  onChange,
  onRemove,
  depth,
}: {
  node: Node;
  fields: DatapointRow[];
  selfKey: string;
  onChange: (next: Node) => void;
  onRemove: () => void;
  depth: number;
}) {
  if (isGroup(node)) {
    return (
      <GroupEditor
        group={node}
        fields={fields}
        selfKey={selfKey}
        onChange={onChange}
        onRemove={onRemove}
        depth={depth}
      />
    );
  }
  return <RuleEditor rule={node} fields={fields} selfKey={selfKey} onChange={onChange} onRemove={onRemove} />;
}

function GroupEditor({
  group,
  fields,
  selfKey,
  onChange,
  onRemove,
  depth,
}: {
  group: Group;
  fields: DatapointRow[];
  selfKey: string;
  onChange: (next: Node) => void;
  onRemove: () => void;
  depth: number;
}) {
  const patch = (rules: Node[]) => onChange({ ...group, rules });

  return (
    <div className="cond__group" data-depth={depth}>
      <div className="cond__grouphead">
        {/* Przełącznik zamiast dwóch pól: AND i OR to wybór, nie ustawienie. */}
        <div className="cond__logic" role="radiogroup" aria-label="Łączenie warunków">
          {(["AND", "OR"] as const).map((logic) => (
            <button
              key={logic}
              type="button"
              role="radio"
              aria-checked={group.logic === logic}
              className={`cond__logicbtn${group.logic === logic ? " cond__logicbtn--on" : ""}`}
              onClick={() => onChange({ ...group, logic })}
            >
              {logic === "AND" ? "wszystkie" : "dowolny"}
            </button>
          ))}
        </div>
        <span className="adm__muted">
          {group.logic === "AND" ? "muszą być spełnione" : "z warunków wystarczy"}
        </span>
        <span className="adm__spacer" />
        <button className="btn btn--ghost" onClick={onRemove}>
          Usuń grupę
        </button>
      </div>

      <div className="cond__rules">
        {group.rules.map((child, at) => (
          <NodeEditor
            key={at}
            node={child}
            fields={fields}
            selfKey={selfKey}
            depth={depth + 1}
            onChange={(next) => patch(group.rules.map((item, i) => (i === at ? next : item)))}
            onRemove={() => {
              const rest = group.rules.filter((_, i) => i !== at);
              // Grupa bez warunków nie znaczy nic — zwijamy ją zamiast zostawiać pustą.
              if (rest.length === 0) onRemove();
              else patch(rest);
            }}
          />
        ))}
      </div>

      <div className="cond__actions">
        <button className="btn btn--secondary" onClick={() => patch([...group.rules, emptyRule()])}>
          + Warunek
        </button>
        <button
          className="btn btn--secondary"
          onClick={() => patch([...group.rules, { logic: "AND", rules: [emptyRule()] }])}
        >
          + Zagnieżdżona grupa
        </button>
      </div>
    </div>
  );
}

function RuleEditor({
  rule,
  fields,
  selfKey,
  onChange,
  onRemove,
}: {
  rule: Rule;
  fields: DatapointRow[];
  selfKey: string;
  onChange: (next: Node) => void;
  onRemove: () => void;
}) {
  const arity = ARITY.get(rule.op) ?? "one";
  const target = useMemo(() => fields.find((item) => item.key === rule.f), [fields, rule.f]);
  const options = target?.options ?? null;

  const setOp = (op: string) => {
    const next = ARITY.get(op) ?? "one";
    // Wartość musi pasować do operatora: lista dla „jest jedną z", nic dla „jest puste".
    if (next === "none") onChange({ f: rule.f, op });
    else if (next === "many") onChange({ f: rule.f, op, v: Array.isArray(rule.v) ? rule.v : [] });
    else onChange({ f: rule.f, op, v: Array.isArray(rule.v) ? (rule.v[0] ?? "") : (rule.v ?? "") });
  };

  const selfReference = Boolean(rule.f) && rule.f === selfKey;

  return (
    <div className="cond__rule">
      <select
        className="select cond__field"
        value={rule.f}
        onChange={(event) => onChange({ ...rule, f: event.target.value })}
      >
        <option value="">— wybierz pole —</option>
        {fields.map((item) => (
          <option key={item.key} value={item.key}>
            {item.key}
            {item.ui_params?.title?.pl ? ` — ${item.ui_params.title.pl}` : ""}
          </option>
        ))}
      </select>

      <select className="select cond__op" value={rule.op} onChange={(event) => setOp(event.target.value)}>
        {OPERATORS.map((entry) => (
          <option key={entry.op} value={entry.op}>
            {entry.label}
          </option>
        ))}
      </select>

      {arity === "one" && <ValueInput rule={rule} options={options} onChange={onChange} />}
      {arity === "many" && <ManyValues rule={rule} options={options} onChange={onChange} />}
      {arity === "window" && <WindowValue rule={rule} onChange={onChange} />}
      {arity === "none" && <span className="cond__novalue adm__muted">—</span>}

      <button className="btn btn--ghost cond__drop" onClick={onRemove} aria-label="Usuń warunek">
        ✕
      </button>

      {selfReference && (
        <p className="cond__warn">
          Warunek odwołuje się do tego samego pola — algorytm rozważa tylko pytania jeszcze
          niewypełnione, więc taki warunek nigdy się nie spełni.
        </p>
      )}
      {rule.f && !target && (
        <p className="cond__warn">Pole „{rule.f}" nie istnieje w schemacie.</p>
      )}
      {/* „nie równa się" / „nie jest żadną z" nie spełniają się na PUSTYM polu.
          Gdy pole ma własny warunek (nie każdy na nie odpowiada), pytanie zależne
          od niego może po cichu nie pojawić się nikomu — tak zniknęło pytanie
          o status studenta: polegało na polu zadawanym tylko posiadaczom wizy. */}
      {(rule.op === "not_equal_to" || rule.op === "is_not_one_of") && Boolean(target?.conditions) && (
        <p className="cond__warn" data-testid="cond-empty-hint">
          Puste pole nie spełnia tego warunku. Pytanie „{rule.f}" nie jest zadawane każdemu —
          jeśli brak odpowiedzi ma też przepuszczać, dodaj grupę „dowolny" z warunkiem
          „jest puste" obok tego.
        </p>
      )}
    </div>
  );
}

function ValueInput({
  rule,
  options,
  onChange,
}: {
  rule: Rule;
  options: DatapointRow["options"];
  onChange: (next: Node) => void;
}) {
  const current = Array.isArray(rule.v) ? "" : String(rule.v ?? "");

  // Pole z listą opcji: wybór zamiast wpisywania. Literówka w wartości jest
  // niewidoczna dla walidatora — po prostu warunek nigdy nie trafia.
  if (options && options.length > 0) {
    return (
      <select
        className="select cond__value"
        value={current}
        onChange={(event) => onChange({ ...rule, v: event.target.value })}
      >
        <option value="">— wybierz —</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.id}
            {option.label?.pl ? ` — ${option.label.pl}` : ""}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      className="input cond__value"
      value={current}
      placeholder="wartość"
      onChange={(event) => onChange({ ...rule, v: event.target.value })}
    />
  );
}

/** „26 years" → {n:"26", unit:"year"}. Jednostkę sprowadzamy do liczby pojedynczej. */
function parseWindow(value: unknown): { n: string; unit: string } {
  const parts = String(value ?? "").trim().split(/\s+/);
  if (parts.length === 2 && /^-?\d+$/.test(parts[0])) {
    return { n: parts[0], unit: parts[1].toLowerCase().replace(/s$/, "") };
  }
  return { n: "", unit: "year" };
}

// Okno dla „w ciągu ostatnich / najbliższych": liczba + jednostka zamiast gołego
// pola tekstowego. Rekruter nie musi wiedzieć, że backend czeka napis „26 years"
// po angielsku — składamy go sami. Pusta liczba = brak okna (warunek po prostu
// się nie spełni, bez błędu), tak jak w `conditions._okno`.
function WindowValue({ rule, onChange }: { rule: Rule; onChange: (next: Node) => void }) {
  const { n, unit } = parseWindow(rule.v);
  const emit = (nextN: string, nextUnit: string) => {
    const clean = nextN.trim();
    onChange({ ...rule, v: clean ? `${clean} ${nextUnit}s` : "" });
  };

  return (
    <span className="cond__value" style={{ display: "inline-flex", gap: 8 }}>
      <input
        className="input"
        style={{ width: "6rem" }}
        type="number"
        min={0}
        value={n}
        placeholder="np. 26"
        onChange={(event) => emit(event.target.value, unit || "year")}
      />
      <select
        className="select"
        value={unit || "year"}
        onChange={(event) => emit(n, event.target.value)}
      >
        {WINDOW_UNITS.map((item) => (
          <option key={item.unit} value={item.unit}>
            {item.label}
          </option>
        ))}
      </select>
    </span>
  );
}

function ManyValues({
  rule,
  options,
  onChange,
}: {
  rule: Rule;
  options: DatapointRow["options"];
  onChange: (next: Node) => void;
}) {
  const values = Array.isArray(rule.v) ? (rule.v as unknown[]).map(String) : [];
  const toggle = (id: string) =>
    onChange({
      ...rule,
      v: values.includes(id) ? values.filter((item) => item !== id) : [...values, id],
    });

  if (options && options.length > 0) {
    return (
      <div className="cond__value cond__chips">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={values.includes(option.id)}
            className={`cond__chip${values.includes(option.id) ? " cond__chip--on" : ""}`}
            onClick={() => toggle(option.id)}
          >
            {option.id}
          </button>
        ))}
      </div>
    );
  }

  return (
    <input
      className="input cond__value"
      value={values.join(", ")}
      placeholder="wartości po przecinku"
      onChange={(event) =>
        onChange({
          ...rule,
          v: event.target.value.split(",").map((part) => part.trim()).filter(Boolean),
        })
      }
    />
  );
}
